import { css } from '@lastbrand/web-ui-styled-system/css'

// Legacy `mobileOnly` overrides on a desktop default are expressed as Panda's mobile-first
// responsive object form { base, lg } (base = mobile, lg = desktop).

// Base EDD message wrapper. Mirrors .eddMessageWrapper.
export const eddMessageWrapperClass = css({
  display: 'flex',
  flexDirection: 'column',
  // No gap: Figma ItemEDD stacks the two lines with line-height leading only.
  mb: 'space-16',
  color: 'black',
  textStyle: 'body-14',
  width: { base: '315px', lg: 'auto' }, // fixed mobile width; px literal (no size token)
  textAlign: { base: 'left', lg: 'center' },
  alignItems: { base: 'flex-start', lg: 'center' },
  justifyContent: { base: 'flex-start', lg: 'center' },
})

// .eddMessageWrapper__quickATC override (mobile only)
export const eddMessageWrapperQuickATCClass = css({
  textAlign: { base: 'unset' },
  alignItems: { base: 'start' },
  justifyContent: { base: 'start' },
})

// .eddMessageWrapper_newDesignV2 override (mobile only)
export const eddMessageWrapperNewDesignV2Class = css({
  width: { base: 'full' },
  mb: { base: 'space-16' },
})

// .errorMessageWrapper (unable-to-ship row)
export const errorMessageWrapperClass = css({
  color: 'black',
  flexDirection: 'row',
})

// .errorMessageWrapper__wrapStyling
export const errorMessageWrapperWrapStylingClass = css({
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  gap: 'space-4',
})

// .unableToShipActions__wrapStyling
export const unableToShipActionsWrapStylingClass = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 'space-8',
})

// .eddMessageWrapper__callOut (LB_STANDARD_PREMIUM 2-day callout)
export const calloutClass = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  justifyContent: 'flex-start',
  width: 'full',
  textAlign: 'left',
  color: 'black',
  background: 'paperwhite',
  px: { base: 'space-16', lg: 'space-20' },
  py: 'space-20',
  mb: 'space-20',
  textStyle: 'body-14',
})

// .eddMessageWrapper__bold
export const boldClass = css({
  fontWeight: 500,
})

// .eddDefaultContainer (wraps icon + message)
// Exported so the "no optical nudge" decision is pinned by a test rather than by the comment
// below alone. Asserting on the generated class name cannot do that job — `css` is mocked in the
// test env — so the contract lives in this object, the same way the z-index contract lives in an
// exported constant (`review-create-modal-ds.z-index.test.ts`).
export const EDD_CHECK_ICON_STYLE = { mr: 0, height: '1lh' } as const

export const eddDefaultContainerClass = css({
  // The textStyle must live on THIS class, not only on the message div: in the default variant
  // this container is the outermost element (the svg's sibling div carries `wrapperClass()`), so
  // without it the svg would inherit Panda preflight's `line-height: 1.5` and `1lh` would resolve
  // to 24px instead of body-14's 20px. No-op for the four variants nested inside wrapperClass().
  textStyle: 'body-14',
  display: 'flex',
  justifyContent: { base: 'flex-start', lg: 'center' },
  alignItems: { base: 'flex-start' },
  gap: { base: 'space-8' },
  // The icon carries `mr: space-8` for the four sites that render it in a plain div; here the
  // container's own `gap` owns the icon↔text spacing, so zero the icon's margin to avoid stacking
  // (gap + mr) into 16px. Legacy parity: `.eddDefaultContainer .messageIcon { margin-right: 0 }`.
  // STRFRNT-16892 (supersedes the merged #7863 `mt: space-4` nudge — the two must not stack):
  // `height: 1lh` sizes the svg viewport to the text's first line box, so the symmetric check
  // glyph centers on line 1 (preserveAspectRatio keeps it undistorted) instead of top-aligning
  // its 20px box against body-14's half-leading. Note: today 1lh == the icon's 20px box
  // (body-14 is lineheight-20), so this is future-proofing — the visible movement came from
  // dropping the old mt nudge, not from 1lh itself.
  // Falls back to the 20px box (status quo) where `lh` is unsupported.
  // No optical nudge on the icon. STRFRNT-16892 added a `translateY(1px)` here, measured against
  // the text's x-height mass centre (icon 9.9px vs text 10.75px). That band is the wrong target:
  // it is pulled down by the descenders in "zip"/"delivery" and by the underline on the zip-code
  // affordance. Measured against the cap band (cap top -> baseline) the icon already sits ~0.9px
  // low without any transform, and the nudge doubles that to ~1.9px; against the x-height band the
  // two options are 0.38px vs 0.63px apart, i.e. within noise. With no metric clearly favouring it
  // and design reading it as off-centre, the box centres are left to line up on their own.
  '& > svg': EDD_CHECK_ICON_STYLE,
})

// STRFRNT-16780/16898: tightens the line-1 -> "Order within..." gap (Figma spec + prod parity,
// ~2-4px) when the two lines stack (mobile / long cutoff messages). Only applied off the
// single-row desktop cutoff layout, where OrderCutoffTimeDS sits inline instead of on its own line.
export const cutoffStackedGapClass = css({
  mt: '-space-4',
})

// .messageIcon (check icon)
export const messageIconClass = css({
  // `width`/`height` resolve against the `sizes` scale, which has no `space-20` — a spacing token here
  // emits invalid CSS and drops silently. Use a literal so the icon box is actually sized.
  width: '20px',
  height: '20px',
  display: 'inline-block', // legacy icon is inline (vertical-align middle); web-ui icon defaults to block → would stack above the text
  verticalAlign: 'middle',
  mr: 'space-8',
  flexShrink: 0,
})

// .infoIcon — legacy 1.7rem (~17px). No 17px token; keep px.
export const infoIconClass = css({
  width: '17px', // 17px info icon; px literal (no 17px token)
  height: '17px',
  flexShrink: 0,
})

// .eddMessageWithCutoff (desktop flex when cutoff style applies)
export const eddMessageWithCutoffClass = css({
  display: { base: 'block', lg: 'flex' },
})

// .desktopOnlyMsg (the "." separator visible only on desktop in cutoff mode)
export const desktopOnlyMsgClass = css({
  mr: 'space-2',
  display: { base: 'none', lg: 'inline' },
})

// .hideDesktopMsg
export const hideDesktopMsgClass = css({
  display: 'none',
})

// .zipcodeBtnClass__unableToShip — legacy keeps the link text black (`color: inherit`) and only
// the underline grey ($dark-gray-2). Grey just the text-decoration; text stays black.
export const zipLinkUnableToShipCss = css.raw({
  color: 'black',
  textDecorationColor: 'mediumgray',
})

// .zipcodeBtnClass__cutoff offset (object form, merged into TextLink css)
export const zipLinkCutoffCss = css.raw({
  ml: 'space-2',
})

// HolidayMessage row. Mirrors .inStockAndReadyToShipMessage + __holidayMessage.
export const holidayMessageWrapperClass = css({
  color: 'black',
  mt: 0,
  mb: { base: 'space-4', lg: 'space-16' },
  textStyle: 'body-14',
  textAlign: 'center',
  width: { base: '315px', lg: 'auto' }, // fixed mobile width; px literal (no size token)
})

export const holidayMessageInnerClass = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: { base: 'flex-start', lg: 'center' },
})

export const holidayIconClass = css({
  mr: 'space-6', // legacy 0.5rem (5px) -> nearest token space-6
  // `sizes` scale has no `space-20`; use a literal so the icon box is sized (see messageIconClass).
  width: '20px',
  height: '20px',
  flexShrink: 0,
})
